import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Trash2, X, Zap } from "lucide-react";
import { toast } from "react-toastify";
import { createTaskAutomationApi } from "../api/taskAutomationApi";
import { createTaskTemplateApi } from "../api/taskTemplateApi";
import {
    AUTOMATION_ACTIONS,
    AUTOMATION_PRESETS,
    AUTOMATION_TRIGGERS,
    EMPTY_AUTOMATION,
    actionLabel,
    draftToPayload,
    triggerLabel,
} from "../utils/taskAutomations";

const TaskAutomationLibrary = ({
    isOpen,
    onClose,
    apiUrl,
    getToken,
    assignOptions = [],
}) => {
    const api = useMemo(() => createTaskAutomationApi({ apiUrl, getToken }), [apiUrl, getToken]);
    const templateApi = useMemo(
        () => createTaskTemplateApi({ apiUrl, getToken }),
        [apiUrl, getToken]
    );
    const [rules, setRules] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [draft, setDraft] = useState(EMPTY_AUTOMATION);
    const [saving, setSaving] = useState(false);

    const enabledPresets = useMemo(
        () => new Set(rules.filter((row) => row.preset && row.enabled).map((row) => row.preset)),
        [rules]
    );

    const load = async () => {
        try {
            const data = await api.fetchAutomations();
            setRules(data.rules);
        } catch (error) {
            toast.error(error.message);
        }
    };

    useEffect(() => {
        if (!isOpen) return undefined;
        load();
        templateApi.fetchTemplates().then(setTemplates).catch(() => setTemplates([]));
        return undefined;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const save = async () => {
        const payload = draftToPayload(draft);
        if (!payload.name) return toast.error("Name is required");
        if (payload.action === "assign_to" && !payload.action_config.assign_to) {
            return toast.error("Pick an assignee");
        }
        if (payload.action === "spawn_template" && !payload.action_config.template_id) {
            return toast.error("Pick a template");
        }
        setSaving(true);
        try {
            await api.createAutomation(payload);
            setDraft(EMPTY_AUTOMATION);
            toast.success("Automation saved");
            await load();
        } catch (error) {
            toast.error(error.message);
        } finally {
            setSaving(false);
        }
    };

    const toggle = async (row) => {
        try {
            await api.updateAutomation(row.id, { enabled: !row.enabled });
            await load();
        } catch (error) {
            toast.error(error.message);
        }
    };

    const enablePreset = async (preset) => {
        try {
            await api.enablePreset(preset);
            toast.success("Recipe enabled");
            await load();
        } catch (error) {
            toast.error(error.message);
        }
    };

    const remove = async (id) => {
        try {
            await api.deleteAutomation(id);
            toast.success("Automation deleted");
            await load();
        } catch (error) {
            toast.error(error.message);
        }
    };

    const needsMinutes =
        draft.trigger === "paused_too_long" || draft.trigger === "unassigned_too_long";

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="task-template-library"
                    onClick={onClose}
                >
                    <motion.div
                        initial={{ y: 24, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 24, opacity: 0 }}
                        className="task-template-library__panel"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <header className="task-template-library__head">
                            <h2>Automations</h2>
                            <button type="button" onClick={onClose} aria-label="Close">
                                <X size={18} />
                            </button>
                        </header>

                        <p className="task-template-library__hint">
                            If this, then that. Time rules run about once a minute and fire at
                            most once per task per day. Completes and creates run immediately.
                        </p>

                        <div className="task-automation__recipes" aria-label="Recipes">
                            {AUTOMATION_PRESETS.map((preset) => {
                                const on = enabledPresets.has(preset.preset);
                                return (
                                    <button
                                        key={preset.preset}
                                        type="button"
                                        className={`task-automation__recipe ${on ? "is-on" : ""}`}
                                        onClick={() => !on && enablePreset(preset.preset)}
                                        disabled={on}
                                    >
                                        <Zap size={14} />
                                        <span>
                                            <strong>{preset.name}</strong>
                                            <em>{on ? "On" : preset.hint}</em>
                                        </span>
                                    </button>
                                );
                            })}
                        </div>

                        <ul className="task-template-library__list">
                            {rules.length === 0 && (
                                <li className="task-template-library__empty">
                                    No automations yet. Enable a recipe or add one below.
                                </li>
                            )}
                            {rules.map((row) => (
                                <li key={row.id} className="task-template-library__item">
                                    <div>
                                        <strong>{row.name}</strong>
                                        <span>
                                            {triggerLabel(row.trigger)} → {actionLabel(row.action)}
                                            {row.enabled ? "" : " · off"}
                                        </span>
                                    </div>
                                    <div className="task-template-library__item-actions">
                                        <button type="button" onClick={() => toggle(row)}>
                                            {row.enabled ? "Disable" : "Enable"}
                                        </button>
                                        <button
                                            type="button"
                                            aria-label="Delete automation"
                                            onClick={() => remove(row.id)}
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                </li>
                            ))}
                        </ul>

                        <div className="task-template-library__form">
                            <h3>New rule</h3>
                            <input
                                type="text"
                                placeholder="Name (e.g. Overdue high to manager)"
                                value={draft.name}
                                onChange={(e) => setDraft((p) => ({ ...p, name: e.target.value }))}
                            />
                            <select
                                value={draft.trigger}
                                onChange={(e) =>
                                    setDraft((p) => ({ ...p, trigger: e.target.value }))
                                }
                                aria-label="When"
                            >
                                {AUTOMATION_TRIGGERS.map((row) => (
                                    <option key={row.id} value={row.id}>
                                        When: {row.label}
                                    </option>
                                ))}
                            </select>
                            {needsMinutes && (
                                <input
                                    type="number"
                                    min={5}
                                    step={5}
                                    value={draft.minutes}
                                    onChange={(e) =>
                                        setDraft((p) => ({ ...p, minutes: e.target.value }))
                                    }
                                    aria-label="Minutes"
                                    placeholder="Minutes"
                                />
                            )}
                            {(draft.trigger === "task_overdue" ||
                                draft.trigger === "task_created") && (
                                <select
                                    value={draft.priority}
                                    onChange={(e) =>
                                        setDraft((p) => ({ ...p, priority: e.target.value }))
                                    }
                                    aria-label="Priority filter"
                                >
                                    <option value="">Any priority</option>
                                    <option value="high">High only</option>
                                    <option value="medium">Medium only</option>
                                    <option value="low">Low only</option>
                                </select>
                            )}
                            <select
                                value={draft.action}
                                onChange={(e) =>
                                    setDraft((p) => ({ ...p, action: e.target.value }))
                                }
                                aria-label="Then"
                            >
                                {AUTOMATION_ACTIONS.map((row) => (
                                    <option key={row.id} value={row.id}>
                                        Then: {row.label}
                                    </option>
                                ))}
                            </select>
                            {(draft.action === "comment" || draft.action === "escalate") && (
                                <textarea
                                    rows={2}
                                    placeholder="Comment text"
                                    value={draft.comment}
                                    onChange={(e) =>
                                        setDraft((p) => ({ ...p, comment: e.target.value }))
                                    }
                                />
                            )}
                            {(draft.action === "assign_to" ||
                                draft.action === "spawn_template") && (
                                <select
                                    value={draft.assign_to}
                                    onChange={(e) =>
                                        setDraft((p) => ({ ...p, assign_to: e.target.value }))
                                    }
                                    aria-label="Assign to"
                                >
                                    <option value="">
                                        {draft.action === "spawn_template"
                                            ? "Keep current assignee"
                                            : "Assign to…"}
                                    </option>
                                    {assignOptions.map((opt) => (
                                        <option key={opt.value} value={opt.value}>
                                            {opt.label.split(" (")[0]}
                                        </option>
                                    ))}
                                </select>
                            )}
                            {draft.action === "set_priority" && (
                                <select
                                    value={draft.set_priority}
                                    onChange={(e) =>
                                        setDraft((p) => ({ ...p, set_priority: e.target.value }))
                                    }
                                    aria-label="Set priority"
                                >
                                    <option value="high">High</option>
                                    <option value="medium">Medium</option>
                                    <option value="low">Low</option>
                                </select>
                            )}
                            {draft.action === "spawn_template" && (
                                <select
                                    value={draft.template_id}
                                    onChange={(e) =>
                                        setDraft((p) => ({ ...p, template_id: e.target.value }))
                                    }
                                    aria-label="Template"
                                >
                                    <option value="">Pick a template…</option>
                                    {templates.map((tpl) => (
                                        <option key={tpl.id} value={tpl.id}>
                                            {tpl.name}
                                        </option>
                                    ))}
                                </select>
                            )}
                            <button
                                type="button"
                                className="task-template-library__save"
                                onClick={save}
                                disabled={saving}
                            >
                                <Plus size={14} />
                                Save rule
                            </button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default TaskAutomationLibrary;
